import { useEffect, useState, useCallback, Fragment, useRef } from "react";
import { Helmet } from "react-helmet";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import { useSearchParams } from "react-router-dom";
import showImages from "../constants/showImages"; 
import { ShowDescriptionModal } from "../components/ShowDescriptionModal";
import { KalendariumModal } from "../components/KalendariumModal";

import "../style/pages/player.css";
import "../style/pages/switcher.css";
import "../style/pages/history.css";
import "../style/pages/timetable.css";
import "../style/pages/kalendarium.css";

export function Player(propsIn) {
  const [props, setProps] = useState({});
  const [params] = useSearchParams();
  const [dj, setDJ] = useState();
  const [nowPlaying, setNowPlaying] = useState();
  const [currentShow, setCurrentShow] = useState("");
  const [ticking, setTicking] = useState(true);
  const [count, setCount] = useState(0);
  const [audioUrlState, setAudioUrlState] = useState("");
  const [state, setState] = useState("paused");
  const [volume, setVolume] = useState(100);
  const [showModalTitle, setShowModalTitle] = useState(null);

  const handleShowClick = useCallback((title) => {
    if (title) setShowModalTitle(title);
  }, [setShowModalTitle]);

  useEffect(() => {
    setProps(propsIn);
    if (props !== propsIn) {
      setNowPlaying();
      setDJ();
    }
  }, [props, propsIn]);

  useEffect(() => {
    if (params.get("oled") !== null) {
      document.body.classList.add("oled");
    } else if (document.body.classList.contains("oled")) {
      document.body.classList.remove("oled");
    }
  }, [params]);

  useEffect(() => {
    fetch(props.apiUrl)
      .then((data) => data.json())
      .then((res) => {
        let outNow = {};
        let outDJ = {};

        const nowPlayingData = res.now_playing;
        const liveData = res.live;

        outNow.title = nowPlayingData?.song?.title || "";
        outNow.artists = nowPlayingData?.song?.artist || "";
        outNow.art = nowPlayingData?.song?.art || "";
        outNow.playlist = nowPlayingData?.playlist || "";

        if (!liveData?.is_live) {
          outDJ.displayname = liveData?.streamer_name || "";
          outDJ.avatar = liveData?.art || "";
          setDJ(outDJ);
        }

        setNowPlaying(outNow);
      })
      .catch((error) => {
        console.error('Fetch error: ', error);
      });
  }, [count, props.apiUrl]);

  useEffect(() => {
    fetch(props.apiTimetableUrl)
      .then((data) => data.json())
      .then((res) => {
        const now = new Date();
        const currentSlot = res.find(slot => now >= new Date(slot.start_timestamp * 1000) && now < new Date(slot.end_timestamp * 1000));
        if (currentSlot) {
          setCurrentShow(currentSlot.title);
        }
      })
      .catch((error) => {
        console.error('Fetch error: ', error);
      });
  }, [props.apiTimetableUrl, count]);

  useEffect(() => {
    const timer = setTimeout(() => ticking && setCount(count + 1), 3000);
    return () => clearTimeout(timer);
  }, [count, ticking]);

  function stop() {
    const player = document.querySelector("#audioPlayer");
    if (player) {
      player.pause();
      player.currentTime = 0;
      player.src = "";
    }
    setAudioUrlState("");
    setState("paused");
  }

  const playStop = useCallback(() => {
    const player = document.querySelector("#audioPlayer");
    if (!player) {
      console.error('Audio player not found');
      return;
    }

    if (player.src !== props.audioUrl || audioUrlState === "") {
      setAudioUrlState(props.audioUrl);
      player.src = props.audioUrl;
      player.load();
    }

    player.oncanplaythrough = () => {
      player.play().catch(error => {
        console.error('Error playing the stream:', error);
      });
    };

    if (state === "paused") {
      setState("play");
    } else if (state === "play") {
      stop();
    }
  }, [audioUrlState, state, props.audioUrl]);

  function openSpotify() {
    const spotifyUrl = `https://open.spotify.com/search/${nowPlaying?.title} ${nowPlaying?.artists}`;
    window.open(spotifyUrl, "_blank");
  }

  function volumeChange(e) {
    var player = document.querySelector("#audioPlayer");
    setVolume(e.target.value);
    player.volume = e.target.value / 100;
  }

  useEffect(() => {
    const player = document.querySelector("#audioPlayer");
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: nowPlaying?.title || "Live Stream",
        artist: nowPlaying?.artists || "Various Artists",
        album: "Radio Promil",
        artwork: [{ src: nowPlaying?.art || "default_artwork_url" }]
      });

      navigator.mediaSession.setActionHandler("play", () => {
        setState("play");
        if (player) {
          if (player.src !== props.audioUrl) {
            player.src = props.audioUrl;
            player.load();
          }
          player.play();
        }
      });

      navigator.mediaSession.setActionHandler("pause", () => {
        setState("paused");
        player.pause();
      });

      if (player.duration && player.duration !== Infinity) {
        navigator.mediaSession.setPositionState({
          duration: player.duration,
          playbackRate: player.playbackRate,
          position: player.currentTime
        });
      }
    }
  }, [nowPlaying, props.audioUrl, setState]);



  return (
    <>
      <Helmet defer={false}>
        <meta name="twitter:title" content={props.station} />
        {state === "paused" && audioUrlState === "" && <title>{props.station}</title>}
        {state === "paused" && audioUrlState !== "" && nowPlaying?.artists && (
          <title>{nowPlaying?.title + " - " + nowPlaying?.artists + " | " + props.station}</title>
        )}
        {state === "play" && audioUrlState !== "" && nowPlaying?.artists && (
          <title>{nowPlaying?.title + " - " + nowPlaying?.artists + " | " + props.station}</title>
        )}
        <meta name="description" content={props.station + " live Radio Promil"} />
        <meta name="twitter:description" content={props.station + " live Radio Promil"} />
      </Helmet>
      <section
        id="player"
        onLoad={() => {
          setTicking(true);
        }}
      >
        {dj && (
          <div className="dj">
            {dj?.avatar && !props.apiLive && (
              <img src={dj?.avatar} alt={dj?.avatar + "'s avatar"} className="profilePicture" />
            )}
            {dj?.avatar && props.apiLive && <img src={dj?.avatar} alt={dj?.avatar + "'s avatar"} className="profilePicture" />}
            <div className="about">
              <span className="title">{dj?.displayname}</span>
              <ReactMarkdown className="subTitle">{dj?.details}</ReactMarkdown>
            </div>
          </div>
        )}
        <div className="container">
          <img src={`${process.env.PUBLIC_URL}/promil_logo_wariant_kolor.png`} alt="Radio Promil Logo" className="station-logo" />
          <div className="player">
            <div className="art">
              <img src={nowPlaying?.art} alt={"The artwork of " + nowPlaying?.title + " by " + nowPlaying?.artists} />
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 135.47 135.47">
                <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="24" fill="black">
                  RADIO PROMIL
                </text>
              </svg>
            </div>
            <div className="info">
              <span className="title">{nowPlaying?.title}</span>
              <span className="artist">{nowPlaying?.artists}</span>
              <span className="subTitle">
                <span className="liveText">LIVE:</span>{" "}
                {nowPlaying?.playlist ? (
                  <button
                    className="show-link"
                    onClick={() => setShowModalTitle(nowPlaying.playlist)}
                    aria-label={`Pokaż opis audycji ${nowPlaying.playlist}`}
                  >
                    {nowPlaying.playlist}
                  </button>
                ) : (
                  nowPlaying?.playlist
                )}
              </span>
            </div>
          </div>
          <div className="controls">
            <button
              className="large"
              onClick={() => {
                playStop();
              }}
              title={state === "paused" ? "Play" : "Stop"}
            >
              {state === "paused" ? (
                <svg viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0V0z" fill="none" />
                  <path d="M10 8.64L15.27 12 10 15.36V8.64M8 5v14l11-7L8 5z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 24 24" height="24" viewBox="0 0 24 24">
                  <g>
                    <rect fill="none" height="24" />
                  </g>
                  <g>
                    <g>
                      <rect height="8" width="8" x="8" y="8" />
                      <path d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M12,20c-4.41,0-8-3.59-8-8c0-4.41,3.59-8,8-8 s8,3.59,8,8C20,16.41,16.41,20,12,20z" />
                    </g>
                  </g>
                </svg>
              )}
            </button>
            <button
              className="spotify-button"
              onClick={() => {
                openSpotify();
              }}
              title="Open in Spotify"
            >
              <svg viewBox="0 0 24 24">
                <path d="M12 2.2c-5.42 0-9.8 4.38-9.8 9.8s4.38 9.8 9.8 9.8 9.8-4.38 9.8-9.8-4.38-9.8-9.8-9.8zm4.3 14.3c-.15.22-.42.3-.67.2-1.83-.73-4.13-1.03-6.85-.3-.25.07-.52-.05-.6-.3-.08-.25.05-.52.3-.6 2.92-.77 5.44-.44 7.46.34.26.1.37.39.26.66zm.95-2.49c-.17.28-.54.39-.82.21-2.09-1.26-5.28-1.63-7.75-.56-.29.13-.63.02-.77-.27-.13-.29-.02-.63.27-.77 2.86-1.26 6.3-.82 8.7.64.28.16.39.54.21.82zm.01-2.56c-.21.34-.65.45-.99.25-2.43-1.4-6.12-1.8-8.56-.62-.34.15-.75-.01-.89-.35-.15-.34.01-.75.35-.89 2.74-1.28 6.82-.84 9.54.75.34.2.45.65.25.99z" />
              </svg>
            </button>
            <div className="right">
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                className="slider"
                id="volume"
                onChange={(e) => {
                  volumeChange(e);
                }}
              />
            </div>
          </div>
        </div>
        <img src={nowPlaying?.art} alt="" className="background animated" />
      </section>
      <audio src={audioUrlState} id="audioPlayer" autoPlay="autoplay" crossOrigin="anonymous" />
      <History apiHistoryUrl={props.apiHistoryUrl} />
      <Timetable
        apiTimetableUrl={props.apiTimetableUrl}
        onShowClick={handleShowClick}
        currentShow={currentShow}
      />
      <Kalendarium />
      {showModalTitle && (
        <ShowDescriptionModal
          showTitle={showModalTitle}
          onClose={() => setShowModalTitle(null)}
        />
      )}
    </>
  );
}

function History(props) {
  const [api, setAPI] = useState();
  const [history, setHistory] = useState();
  const [ticking, setTicking] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    setAPI((url) => {
      if (props.apiHistoryUrl === url) {
        setHistory();
        return props.apiHistoryUrl;
      } else {
        return url;
      }
    });

    fetch(props.apiHistoryUrl).then((data) => {
      if (!data.ok) {
        throw new Error('Network response was not ok');
      }
      return data.json();
    })
      .then((res) => {
        const filteredHistory = res.song_history.filter(item => {
          const artist = item.song.artist;
          const title = item.song.title;
          return !(artist === "0,7 po 20" && title === "0,7 po 20") && !(artist === "Rap Gie" && title === "Rap Gie");
        });
        setHistory(filteredHistory);
      })
      .catch((error) => {
        console.error('Fetch error: ', error);
      });
  }, [count, props.apiHistoryUrl, api]);

  useEffect(() => {
    const timer = setTimeout(() => ticking && setCount(count + 1), 3000);
    return () => clearTimeout(timer);
  }, [count, ticking]);

  return (
    <>
      <section id="history">
        <div className="container">
          <h2>Historia utworów</h2>
          <ul>
            {history && history.map((item, index) => (
              <li key={index}>
                {item?.song?.art && <img src={item.song.art} alt="" />}
                <div className="info">
                  {item.song.artist && <span className="subTitle">{item.song.artist}</span>}
                  <span className="title">{item.song.title}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}


function Timetable(props) {
  const [displayDate, setDisplayDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [filteredTimetable, setFilteredTimetable] = useState([]);
  const [ticking, setTicking] = useState(true);
  const [count, setCount] = useState(0);

  // Define excluded names
  const excludedNames = ["Audycja Rap Gie", "0,7 po 20 Piątek", "0,7 po 20 Sobota", "0,7 po 20 Piątek - Koniec", "0,7 po 20 Sobota - Koniec"];

  useEffect(() => {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + 6); // To include a full week ahead

    // Formatting start and end date to include time and timezone
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();

    const apiUrl = `${props.apiTimetableUrl}&start=${formattedStartDate}&end=${formattedEndDate}`;

    fetch(apiUrl).then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
      .then(data => {
        // Sort the timetable entries by start timestamp before setting them to state
        const sortedData = data.sort((a, b) => a.start_timestamp - b.start_timestamp);
        setSlots(sortedData);
        filterTimetable(sortedData, new Date(displayDate)); // Initial filter for today
      })
      .catch(error => {
        console.error('Fetch error:', error);
      });
  }, [props.apiTimetableUrl, displayDate]);

  useEffect(() => {
    filterTimetable(slots, displayDate);
  }, [displayDate, slots]);

  useEffect(() => {
    const timer = setTimeout(() => ticking && setCount(count + 1), 20000);
    return () => clearTimeout(timer);
  }, [count, ticking]);

  function filterTimetable(timetableSlots, date) {
    const filtered = timetableSlots.filter(slot => {
      const slotDate = new Date(slot.start_timestamp * 1000);
      const isDateMatch = slotDate.toDateString() === date.toDateString();
      const isExcluded = excludedNames.includes(slot.name);
      return isDateMatch && !isExcluded;
    });
    setFilteredTimetable(filtered);
  }

  function addDay() {
    const newDate = new Date(displayDate.setDate(displayDate.getDate() + 1));
    setDisplayDate(new Date(newDate));
  }

  function removeDay() {
    const newDate = new Date(displayDate.setDate(displayDate.getDate() - 1));
    setDisplayDate(new Date(newDate));
  }

  return (
    <section id="timetable">
      <div className="container">
        <h2>Ramówka</h2>
        <div className="inline">
          <button
            onClick={removeDay}
            className="material-symbols-outlined"
          >
            remove
          </button>
          <h3> {displayDate.toLocaleDateString("pl", { weekday: "long" })} {displayDate.getDate()} {displayDate.toLocaleDateString("pl", { month: "long" })}{" "}
            {displayDate.getFullYear()}
          </h3>
          <button
            onClick={addDay}
            className="material-symbols-outlined"
          >
            add
          </button>
        </div>
        <ul>
          {filteredTimetable.length > 0 ? (
            filteredTimetable.map((slot, index) => (
              <Fragment key={index}>
                <TimetableItem slot={slot} onShowClick={props.onShowClick} currentShow={props.currentShow} />
              </Fragment>
            ))
          ) : (
            <p>Nie ma jeszcze ramówki na dany dzień.</p>
          )}
        </ul>
      </div>
    </section>
  );
}


function TimetableItem(props) {
  const [date, setDate] = useState();
  const [endDate, setEndDate] = useState();

  function addZero(i) {
    if (i < 10) {
      i = "0" + i;
    }
    return i;
  }

  useEffect(() => {
    if (!props.slot?.start_timestamp) return;

    setDate(new Date(props.slot.start_timestamp * 1000));
    setEndDate(new Date(props.slot.end_timestamp * 1000));
  }, [props.slot]);

  const showTitle = props.slot.title || props.slot.name || "";
  const showArt = showImages[showTitle] ? `${process.env.PUBLIC_URL}/${showImages[showTitle]}` : "";

  const isCurrent = props.currentShow && showTitle === props.currentShow;

  const onKey = (e) => {
    if (!props.onShowClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      props.onShowClick(showTitle);
    }
  };

  const handleClick = () => {
    if (props.onShowClick) props.onShowClick(showTitle);
  };

  return (
    <li
      className={"large-slot" + (isCurrent ? " now-playing" : "")}
      aria-current={isCurrent ? "true" : undefined}
      onClick={handleClick}
      onKeyDown={onKey}
      role={props.onShowClick ? "button" : undefined}
      tabIndex={props.onShowClick ? 0 : undefined}
    >
      {showArt && <img src={showArt} alt={`${showTitle} art`} />}
      <div className="info">
        <div className="inline">
          <span className="title">{showTitle}</span>
          {date && endDate && (
            <span className="date">
              {addZero(date.getHours())}:{addZero(date.getMinutes())} - {addZero(endDate.getHours())}:{addZero(endDate.getMinutes())}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function Kalendarium() {
  const [events, setEvents] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [dateParam, setDateParam] = useState(getTodayParam());
  const [headerDate, setHeaderDate] = useState(getTodayLabel());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setDateParam(getTodayParam());
      setHeaderDate(getTodayLabel());
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const url = buildEventsUrl(dateParam);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        const filtered = normalizeEvents(data);
        const featuredEvent = filtered.find((event) => event.is_featured && event.cover_url) || null;
        setFeatured(featuredEvent);
        setEvents(filtered);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Fetch error:", error);
        }
      });

    return () => controller.abort();
  }, [dateParam]);

  const openEventModal = (event) => {
    setSelectedEvent(event);
  };

  return (
    <section id="kalendarium">
      {featured?.cover_url && (
        <img src={featured.cover_url} alt="" className="background animated" />
      )}
      <div className="container">
        <div className="kalendarium-header">
          <h2>Kalendarium</h2>
          <span className="kalendarium-date">{headerDate}</span>
        </div>
        <div className="kalendarium-grid">
          <div className="kalendarium-featured">
            {featured ? (
              <>
                {featured.cover_url && (
                  <div className="kalendarium-featured-image">
                    <img src={featured.cover_url} alt={featured.date_str} />
                  </div>
                )}
                <span className="title">{formatEventTitle(featured)}</span>
                <p className="description">{formatEventText(featured.text)}</p>
                <button
                  className="more-button"
                  onClick={() => openEventModal(featured)}
                  aria-label={`Więcej: ${featured.date_str}`}
                >
                  Więcej
                </button>
              </>
            ) : (
              <p>Brak wyróżnionego wydarzenia.</p>
            )}
          </div>
          <div className="kalendarium-list-wrapper">
            <button
              className="kalendarium-scroll"
              onClick={() => scrollList(listRef.current, -1)}
              aria-label="Przewiń w lewo"
              type="button"
            >
              ‹
            </button>
            <ul className="kalendarium-list" ref={listRef}>
            {events.length > 0 ? (
              events.map((event) => (
                <KalendariumItem
                  key={event.id}
                  event={event}
                  onMore={() => openEventModal(event)}
                />
              ))
            ) : (
              <p>Brak wydarzeń na dziś.</p>
            )}
            </ul>
            <button
              className="kalendarium-scroll"
              onClick={() => scrollList(listRef.current, 1)}
              aria-label="Przewiń w prawo"
              type="button"
            >
              ›
            </button>
          </div>
        </div>
      </div>
      {selectedEvent && (
        <KalendariumModal
          event={selectedEvent}
          title={formatEventTitle(selectedEvent)}
          text={formatEventText(selectedEvent.text)}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </section>
  );
}

function KalendariumItem({ event, onMore }) {
  const descriptionRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (!descriptionRef.current) return;
    const el = descriptionRef.current;
    const checkOverflow = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    checkOverflow();
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [event?.text]);

  return (
    <li className="kalendarium-item">
      <span className="title">{formatEventTitle(event)}</span>
      <span className="description" ref={descriptionRef}>{formatEventText(event.text)}</span>
      {isOverflowing && (
        <button
          className="more-button"
          onClick={onMore}
          aria-label={`Więcej: ${event.date_str}`}
        >
          Więcej
        </button>
      )}
    </li>
  );
}

function scrollList(listEl, direction) {
  if (!listEl) return;
  const scrollAmount = listEl.clientWidth;
  listEl.scrollBy({ left: scrollAmount * direction, behavior: "smooth" });
}

function formatEventText(text) {
  if (!text) return "";
  return text.replace(/\s*\[[^\]]+\]\s*$/, "").trim();
}

function formatEventTitle(event) {
  if (!event) return "";
  const anniversary = getAnniversaryLabel(event.parsed_date);
  return anniversary ? `${event.date_str} · ${anniversary}` : event.date_str;
}

function getAnniversaryLabel(parsedDate) {
  if (!parsedDate) return null;
  const parts = parsedDate.split(".");
  const year = Number(parts[2]);
  if (!year) return null;
  const currentYear = new Date().getFullYear();
  const diff = currentYear - year;
  if (diff <= 0) return null;
  return `${diff}. rocznica`;
}

function getTodayParam() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${month}-${day}`;
}

function buildEventsUrl(dateParam) {
  return `/events?date=${dateParam}`;
}

function normalizeEvents(data) {
  const rawEvents = Array.isArray(data) ? data : data?.events || data?.data || [];
  return rawEvents.filter((event, index) => {
    if (!event) return false;
    return !(index === 0 && !event.parsed_date);
  });
}

function getTodayLabel() {
  return new Date().toLocaleDateString("pl", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}
